import { change } from "redux-form";
import LocalStorage from "utils/localstorage.util";
import { AppDispatch } from "redux/store";

type PlaceOption = {
  value?: string;
  details?: {
    name?: string;
    place_id?: string;
    formatted_address?: string;
    airport?: boolean;
    city?: string;
    country_name_code?: string;
    geometry?: {
      location?: { lat: number; lng: number };
    };
  };
};

export function getHourlyDuration(
  bookingFormPayload?: { duration?: number | string } | null,
  pickUpFormValues?: { duration?: number | string } | null
) {
  const raw = bookingFormPayload?.duration ?? pickUpFormValues?.duration;
  const duration = Number(raw);
  return Number.isFinite(duration) && duration > 0 ? duration : null;
}

export function syncHourlyDurationAfterModal(
  dispatch: AppDispatch,
  duration: number | string
) {
  const normalized = String(duration);

  dispatch(change("PickUpForm", "duration", normalized));

  const storedPickUpForm = LocalStorage.getItem("PickUpForm");
  if (storedPickUpForm && typeof storedPickUpForm === "object") {
    LocalStorage.setItem("PickUpForm", {
      ...storedPickUpForm,
      duration: normalized,
    });
  }
}

export function appendDestinationToHourlyPriceRequest(
  data: Record<string, any>,
  destination: PlaceOption,
  duration?: number | string
) {
  const location = destination?.details?.geometry?.location;
  const payload = { ...data };

  if (duration != null && duration !== "") {
    payload.duration = Number(duration);
  }

  payload.destination = destination.value;
  payload.coordinates = [...(payload.coordinates || [])];
  if (location) {
    payload.coordinates.push([location.lng, location.lat]);
    payload.destinationCoordinates = [location.lat, location.lng];
  }

  payload.country_short_name_dropoff =
    destination?.details?.country_name_code || payload.country_short_name_dropoff;
  payload.dropoff_type = destination?.details?.airport ? "airport" : "place";

  if (Array.isArray(payload.city_name) && destination?.details?.city) {
    payload.city_name = [...payload.city_name, destination.details.city];
  }

  payload.destination_details = {
    title: destination?.details?.name || "",
    place_id: destination?.details?.place_id || "",
    address: destination?.details?.formatted_address || destination.value || "",
    coordinates: {
      lat: location?.lat,
      lng: location?.lng,
    },
  };

  return payload;
}

export type GetPricesFlags = {
  needs_destination?: boolean;
  adjust_duration?: boolean;
  message?: string;
  suggested_duration_hours?: number;
  mc_source?: string;
  aggregator_source?: string;
  is_talixo?: boolean;
  aggregator_provider?: string;
};

export function getAggregatorPriceFlags(
  data: GetPricesFlags | null | undefined
) {
  const source = data?.aggregator_source || data?.mc_source || "mc";

  return {
    needsDestination: !!data?.needs_destination,
    adjustDuration: !!data?.adjust_duration,
    message: data?.message || "",
    source,
    isTalixo: data?.is_talixo ?? source === "talixo",
    provider: data?.aggregator_provider || "mc",
  };
}

export function getAggregatorSourcePayload(
  data: GetPricesFlags | null | undefined
) {
  const source = data?.aggregator_source || data?.mc_source || "mc";

  return {
    mc_source: source,
    aggregator_source: source,
    is_talixo: data?.is_talixo ?? source === "talixo",
    aggregator_provider: data?.aggregator_provider || "mc",
  };
}

export function getAggregatorDisplayCurrency(
  priceDetails?: { currency_short?: string; currency_sign?: string },
  isAggregator?: boolean
) {
  if (isAggregator) {
    return priceDetails?.currency_short || priceDetails?.currency_sign || "EUR";
  }

  return priceDetails?.currency_sign || priceDetails?.currency_short || "SAR";
}

export function isAggregatorMcBooking(
  payload?: { aggregator?: boolean } | null
) {
  return !!payload?.aggregator;
}

export function isAggregatorTrip(
  booking?: {
    isAggregator?: boolean;
    aggregator_booking_id?: string;
    is_talixo?: boolean;
    aggregator_source?: string;
    mc_source?: string;
  } | null
) {
  if (!booking) return false;
  if (booking.isAggregator || booking.aggregator_booking_id) return true;
  const source = booking.aggregator_source || booking.mc_source;
  return booking.is_talixo === true || source === "talixo" || source === "mc";
}

export function clearAggregatorPromoFormFields(dispatch: AppDispatch) {
  const fields = [
    "coupon_code",
    "discount",
    "discount_type",
    "discounted_price",
    "is_yelo_points",
  ] as const;

  fields.forEach((field) => {
    dispatch(change("SearchForm", field, field === "is_yelo_points" ? false : ""));
  });
}

export function stripAggregatorPromoFromBookingData(data: Record<string, any>) {
  delete data.coupon_code;
  delete data.discount;
  delete data.discount_type;
  delete data.discounted_price;
  delete data.is_yelo_points;
  return data;
}

const normalizeCoordinatePair = (
  pair: unknown
): [number, number] | undefined => {
  if (!Array.isArray(pair) || pair.length < 2) return undefined;
  const a = Number(pair[0]);
  const b = Number(pair[1]);
  if (!Number.isFinite(a) || !Number.isFinite(b)) return undefined;
  return [a, b];
};

/** Strip UI-only fields and normalize coords before POST /trip/getprices. */
export function buildGetPricesRequest(
  payload: Record<string, any> | null | undefined
) {
  if (!payload || typeof payload !== "object") return {};

  const coordinates = Array.isArray(payload.coordinates)
    ? payload.coordinates
        .map(normalizeCoordinatePair)
        .filter((pair): pair is [number, number] => !!pair)
    : undefined;

  const request: Record<string, any> = {
    pickup: payload.pickup,
    destination: payload.destination,
    time_stamp: payload.time_stamp,
    trip_type: payload.trip_type,
    duration: payload.duration,
    pickup_type: payload.pickup_type,
    dropoff_type: payload.dropoff_type,
    country_short_name: payload.country_short_name,
    country_short_name_dropoff: payload.country_short_name_dropoff,
    city_name: payload.city_name,
    destination_details: payload.destination_details,
  };

  if (coordinates?.length) {
    request.coordinates = coordinates;
  }

  const pickupCoordinates = normalizeCoordinatePair(payload.pickupCoordinates);
  if (pickupCoordinates) {
    request.pickupCoordinates = pickupCoordinates;
  }

  const destinationCoordinates = normalizeCoordinatePair(
    payload.destinationCoordinates
  );
  if (destinationCoordinates) {
    request.destinationCoordinates = destinationCoordinates;
  }

  return request;
}
